// src/modules/tickets/pages/TicketPrintView.tsx
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Print as PrintIcon, ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { getTenantSettings, TenantSettings } from "../../../services/settings";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";

export default function TicketPrintView() {
  useProtectedRoute();
  const { token } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const [ticket, setTicket] = useState<any>(null);
  const [shop, setShop] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState<'classic' | 'compact'>("classic");

  const ticketUrl = useMemo(() => `${window.location.origin}/tickets/${id}`, [id]);

  // Prebuilt, zero-dependency image sources
  // QR: qrserver.com; Barcode: bwipjs API (CODE128)
  const qrSrc = useMemo(
    () => `https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(ticketUrl)}`,
    [ticketUrl]
  );
  const barcodeSrc = useMemo(() => {
    const text = encodeURIComponent(String((ticket && ticket.ticketNumber) || id || ""));
    return `https://bwipjs-api.metafloor.com/?bcid=code128&text=${text}&scale=2&height=10&includetext=1`;
  }, [ticket, id]);

  const heroGradient = useMemo(
    () =>
      `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 55%, #0f172a 100%)`,
    [theme]
  );
  const surfaceBorder = `1px solid ${theme.palette.mode === "light" ? "#e5e7eb" : theme.palette.divider}`;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [ticketRes, shopRes] = await Promise.all([
          api.get(`/tickets/${id}`, { headers }),
          api.get(`/shops/me`, { headers }),
        ]);
        setTicket(ticketRes.data);
        setShop(shopRes.data);
      } catch {
        /* keep minimal UI */
      } finally {
        setLoading(false);
      }
    };
    if (token && id) fetchData();
  }, [id, token]);

  useEffect(() => {
    (async () => {
      if (!token) return;
      try {
        const s: TenantSettings = await getTenantSettings(token);
        setTemplate((s.templates?.ticketTemplate as any) || "classic");
      } catch {}
    })();
  }, [token]);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading…</Typography>
      </Box>
    );
  }

  if (!ticket || !shop) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Unable to load ticket.</Typography>
        <Button sx={{ mt: 2 }} onClick={() => navigate("/tickets")}>
          Back to Tickets
        </Button>
      </Box>
    );
  }

  const InfoRow = ({ label, value }: { label: string; value: any }) => (
    <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 180 }}>
        <strong>{label}:</strong>
      </Typography>
      <Typography variant="body2" sx={{ flex: 1 }}>
        {value ?? "â€”"}
      </Typography>
    </Stack>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* HERO / HEADER */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          overflow: "hidden",
          border: surfaceBorder,
          background: heroGradient,
          color: "white",
        }}
      >
        <CardContent sx={{ py: { xs: 2.25, md: 2.75 } }}>
          <Grid container spacing={2} alignItems="center" justifyContent="space-between">
            <Grid item xs={12} md="auto">
              <Stack spacing={0.25}>
                <Typography variant={isSmDown ? "h5" : "h4"} sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                  Print Job Ticket
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Ticket&nbsp;# {ticket.ticketNumber}
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md="auto">
              <Stack direction="row" spacing={1} justifyContent={{ xs: "flex-start", md: "flex-end" }}>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate(`/tickets/${id}`)}
                  sx={{
                    color: "white",
                    borderColor: "rgba(255,255,255,0.5)",
                    "&:hover": { borderColor: "white", backgroundColor: "rgba(255,255,255,0.08)" },
                    "@media print": { display: "none" },
                  }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<PrintIcon />}
                  onClick={() => window.print()}
                  sx={{ fontWeight: 800, "@media print": { display: "none" } }}
                >
                  Print
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* PRINT CONTENT */}
      <Paper
        elevation={0}
        sx={{
          p: template === 'compact' ? { xs: 1.5, md: 2 } : { xs: 2, md: 3 },
          borderRadius: 3,
          border: surfaceBorder,
          backgroundColor: theme.palette.background.paper,
        }}
        id="full-ticket-content"
      >
        {/* Header: Shop + QR/Barcode */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md>
            <Stack spacing={0.5}>
              <Typography variant={template === 'compact' ? 'h6' : 'h5'} sx={{ fontWeight: 900, color: "primary.main" }}>
                {shop.tenantName || "Repair Shop"}
              </Typography>
              <Typography variant="body2">{shop.tenantAddress}</Typography>
              <Typography variant="body2">
                {shop.tenantPhone} Â· {shop.tenantEmail}
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md="auto">
            <Stack direction="row" spacing={2} alignItems="center" justifyContent={{ xs: "flex-start", md: "flex-end" }}>
              <Box
                sx={{
                  width: 164,
                  height: 164,
                  border: surfaceBorder,
                  borderRadius: 2,
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "white",
                }}
              >
                <img src={qrSrc} alt="Ticket QR" width={160} height={160} />
              </Box>

              <Box
                sx={{
                  px: 1,
                  py: 1,
                  border: surfaceBorder,
                  borderRadius: 2,
                  backgroundColor: "white",
                }}
              >
                <img
                  src={barcodeSrc}
                  alt={`Barcode ${ticket.ticketNumber || id}`}
                  style={{ display: "block", maxHeight: 64 }}
                />
              </Box>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2.5 }} />

        {/* Client & Job sections */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
              Client Information
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <InfoRow label="Name" value={ticket.clientName} />
              <InfoRow label="Phone" value={ticket.clientPhone} />
              <InfoRow label="Email" value={ticket.clientEmail} />
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
              Meta
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <InfoRow
                label="Created By"
                value={ticket.createdBy?.fullName || ticket.createdBy?.name || ticket.createdBy?.email || "â€”"}
              />
              <InfoRow label="Created At" value={new Date(ticket.createdAt).toLocaleString()} />
              <InfoRow label="Ticket #" value={ticket.ticketNumber} />
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
              Job Details
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <InfoRow label="Job Type" value={ticket.jobType} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoRow label="Device Type" value={ticket.deviceType} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InfoRow label="Brand" value={ticket.brand} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InfoRow label="Model" value={ticket.model} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InfoRow label="Serial / IMEI" value={ticket.serialOrImei || "â€”"} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoRow label="Status" value={ticket.status} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoRow
                    label="Estimated Completion"
                    value={
                      ticket.estimatedFinishDate
                        ? new Date(ticket.estimatedFinishDate).toLocaleDateString()
                        : "â€”"
                    }
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
              Technician Notes
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {ticket.notes || "â€”"}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 3, color: "text.secondary" }}
        >
          <Typography variant="caption">
            Generated by RepairSync · {new Date().toLocaleDateString()}
          </Typography>
          <Typography variant="caption">URL: {ticketUrl}</Typography>
        </Stack>
      </Paper>

      <Box sx={{ height: 12 }} />
    </Box>
  );
}


